import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './useRedux'
import { userStateSelector } from '@/pages/user/selectors'
import { setVerifyEmailModalState } from '@/pages/user/userSlice'
import {
  ROUTE_CLAIM,
  ROUTE_DASHBOARD,
  ROUTE_EXTERNAL_CLAIM,
  ROUTE_EXTERNAL_CREATE_PASSWORD,
  ROUTE_EXTERNAL_LINK_SUCCESS,
  ROUTE_EXTERNAL_LOGIN,
  ROUTE_EXTERNAL_SIGNUP,
  ROUTE_EXTERNAL_THIS_ACCOUNT_IS_ALREADY_TAKEN,
  ROUTE_FORGOT_PASSWORD,
  ROUTE_LIFETIME,
  ROUTE_LOGIN,
  ROUTE_PREMIUM_LOGIN,
  ROUTE_REDEEM_CODE,
  ROUTE_SIGNUP,
  ROUTE_PLAYER_PROFILE,
  ROUTE_DUPR_REPLAY,
  ROUTE_RESET_PASSWORD,
  ROUTE_VERIFY_BASE,
} from '@/constants/Routes'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { authSelector } from '@/pages/auth/selectors'

export function useRouteUtils() {
  useRedirectProtectedPagesToLogin()
  useShowVerifyEmailModal()
}

function useShowVerifyEmailModal() {
  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const { user } = useAppSelector(userStateSelector)
  useEffect(() => {
    if (user) {
      const containsEvents = /events/.test(location.pathname)
      const containsPlayers = /player/.test(location.pathname)
      const containsClubs = /clubs/.test(location.pathname)
      const containsMatch = /match/.test(location.pathname)
      const containsEmailVerify = /email/.test(location.pathname)
      const containsExternalLogin = /login-external-app/.test(location.pathname)
      if (containsEmailVerify || containsExternalLogin) dispatch(setVerifyEmailModalState(false))
      if (!user?.isValidEmail && !containsEvents && !containsPlayers && !containsEmailVerify && !containsExternalLogin)
        dispatch(setVerifyEmailModalState(true))
      if (!user?.isValidEmail && (containsClubs || containsMatch) && !containsEmailVerify) {
        navigate(ROUTE_DASHBOARD)
        dispatch(setVerifyEmailModalState(true))
      }
      if (user?.isValidEmail) dispatch(setVerifyEmailModalState(false))
    }
  }, [user, dispatch, navigate])
}

function useRedirectProtectedPagesToLogin() {
  const { token } = useAppSelector(authSelector)
  const navigate = useNavigate()
  const { user } = useAppSelector(userStateSelector)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const clientKey = searchParams.get('clientKey')

  useEffect(() => {
    if (user) {
      return
    }

    const isEmptyPath = location.pathname === '/' || location.pathname === ''
    const isNotAuthPath =
      location.pathname.includes(ROUTE_CLAIM) ||
      location.pathname.includes(ROUTE_SIGNUP) ||
      location.pathname.includes(ROUTE_FORGOT_PASSWORD) ||
      location.pathname.includes(ROUTE_EXTERNAL_LOGIN) ||
      location.pathname.includes(ROUTE_EXTERNAL_CLAIM) ||
      location.pathname.includes(ROUTE_EXTERNAL_SIGNUP) ||
      location.pathname.includes(ROUTE_EXTERNAL_THIS_ACCOUNT_IS_ALREADY_TAKEN) ||
      location.pathname.includes(ROUTE_EXTERNAL_CREATE_PASSWORD) ||
      location.pathname.includes(ROUTE_LIFETIME) ||
      location.pathname.includes(ROUTE_EXTERNAL_LINK_SUCCESS) ||
      location.pathname.includes(ROUTE_PREMIUM_LOGIN) ||
      location.pathname.includes(ROUTE_REDEEM_CODE) ||
      location.pathname.includes(ROUTE_PLAYER_PROFILE) ||
      location.pathname.includes(ROUTE_DUPR_REPLAY) ||
      location.pathname.includes(ROUTE_RESET_PASSWORD) ||
      // The emailed sign-in link lands here with NO session yet -- the session is what this page
      // fetches. Treating it as a protected page bounced the user to /login before the /verify
      // call could return the token, so the auto-login never completed. It is anonymous by nature,
      // like the reset-password link above.
      location.pathname.includes(ROUTE_VERIFY_BASE)

    const isAuthPath = !isNotAuthPath && !isEmptyPath

    if (!token && isAuthPath && location.pathname !== '/login') {
      const loginLocation = location.pathname.includes(ROUTE_PREMIUM_LOGIN)
        ? `${ROUTE_EXTERNAL_LOGIN}/${clientKey}`
        : ROUTE_LOGIN

      navigate(
        `${loginLocation}?redirect=${encodeURIComponent(location.pathname)}${encodeURIComponent(location.search)}`,
        { relative: 'path', replace: true }
      )
    }
  }, [user, navigate, location.pathname, location.search, token, clientKey])
}
